const normalize = (value) => String(value ?? '').trim();

const rollDie = (sides) => 1 + Math.floor(Math.random() * sides);

const parseIntSafe = (value) => {
  const n = Number(String(value ?? '').trim());
  return Number.isFinite(n) ? n : null;
};

const DICE_GROUP_PATTERN = /\d+\s*d\s*\d+/gi;

// Duality die colours: Hope is yellow, Fear is purple (Daggerheart specification).
export const DUALITY_HOPE_COLOR = '#FFD700';
export const DUALITY_FEAR_COLOR = '#8B5CF6';

export const parseDiceCommand = (raw) => {
  const input = normalize(raw);
  if (!input) {
    return { kind: 'invalid', error: 'Empty command.' };
  }

  if (/^clear$/i.test(input)) {
    return { kind: 'clear' };
  }

  // Duality [±Advantage/Disadvantage] [±N]
  if (/^duality\b/i.test(input)) {
    const tokens = input
      .split(/\s+/)
      .filter(Boolean)
      .map((t) => t.trim());

    const hasPlusAdv = tokens.some((t) => /^\+?advantage$/i.test(t) || /^\+?advatage$/i.test(t));
    const hasMinusDis = tokens.some((t) => /^-?disadvantage$/i.test(t));

    const modifier = tokens
      .filter((t) => /^[+-]\d+$/.test(t))
      .reduce((acc, t) => acc + Number(t), 0);
    const requestedMode = tokens.find((token) => /^(standard|doomed|blessed|epic)$/i.test(token));
    const dualityMode = requestedMode ? requestedMode.toLowerCase() : 'standard';
    const modeSides = {
      standard: { hopeSides: 12, fearSides: 12 },
      doomed: { hopeSides: 12, fearSides: 20 },
      blessed: { hopeSides: 20, fearSides: 12 },
      epic: { hopeSides: 20, fearSides: 20 },
    }[dualityMode];

    // If both are present, prefer the last one written.
    let advMode = 'none';
    const lastAdv = tokens
      .map((t, idx) => ({ t, idx }))
      .filter(({ t }) => /advantage|advatage|disadvantage/i.test(t))
      .pop();
    if (lastAdv) {
      if (/disadvantage/i.test(lastAdv.t)) advMode = 'disadvantage';
      else advMode = 'advantage';
    } else {
      if (hasPlusAdv) advMode = 'advantage';
      if (hasMinusDis) advMode = 'disadvantage';
    }

    return {
      kind: 'duality',
      dualityMode,
      ...modeSides,
      modifier,
      advMode,
    };
  }

  // Guard: more than 2 dice type groups is unsupported
  const diceGroupCount = (input.match(DICE_GROUP_PATTERN) || []).length;
  if (diceGroupCount > 2) {
    return { kind: 'invalid', error: 'Up to 2 types of dice are supported in any given dice box, use another dice box' };
  }

  // Two-dice: NdM +|- NdM [+|- K]
  // Examples: 2d6 + 1d10, 1d20 - 1d4 + 3
  const multiMatch = input.match(/^\s*(\d+)\s*d\s*(\d+)\s*([+-])\s*(\d+)\s*d\s*(\d+)\s*(?:([+-])\s*(\d+))?\s*$/i);
  if (multiMatch) {
    const count1 = parseIntSafe(multiMatch[1]);
    const sides1 = parseIntSafe(multiMatch[2]);
    const sign1 = multiMatch[3];
    const count2 = parseIntSafe(multiMatch[4]);
    const sides2 = parseIntSafe(multiMatch[5]);
    const sign2 = multiMatch[6] || null;
    const modRaw = multiMatch[7] || null;

    if (!count1 || count1 < 1 || count1 > 200) return { kind: 'invalid', error: 'Dice count out of range.' };
    if (!sides1 || sides1 < 2 || sides1 > 1000) return { kind: 'invalid', error: 'Dice sides out of range.' };
    if (!count2 || count2 < 1 || count2 > 200) return { kind: 'invalid', error: 'Dice count out of range.' };
    if (!sides2 || sides2 < 2 || sides2 > 1000) return { kind: 'invalid', error: 'Dice sides out of range.' };

    const modifier = modRaw ? (sign2 === '-' ? -Number(modRaw) : Number(modRaw)) : 0;

    return {
      kind: 'multi',
      dice: [
        { count: count1, sides: sides1, negative: false },
        { count: count2, sides: sides2, negative: sign1 === '-' },
      ],
      modifier,
    };
  }

  // Standard dice: NdM [+|- K]
  // Examples: 1d20, 2d10 + 4, 10d4 - 3
  const match = input.match(/^\s*(\d+)\s*d\s*(\d+)\s*(?:([+-])\s*(\d+))?\s*$/i);
  if (match) {
    const count = parseIntSafe(match[1]);
    const sides = parseIntSafe(match[2]);
    const sign = match[3] || null;
    const modRaw = match[4] || null;

    if (!count || count < 1 || count > 200) {
      return { kind: 'invalid', error: 'Dice count out of range.' };
    }
    if (!sides || sides < 2 || sides > 1000) {
      return { kind: 'invalid', error: 'Dice sides out of range.' };
    }

    const modifier = modRaw ? (sign === '-' ? -Number(modRaw) : Number(modRaw)) : 0;

    return {
      kind: 'dnd',
      count,
      sides,
      modifier,
    };
  }

  return {
    kind: 'invalid',
    error: 'Unrecognized command format.'
  };
};

export const resolveRoll = (parsed) => {
  if (!parsed || parsed.kind === 'invalid') {
    return { ok: false, error: parsed?.error || 'Invalid command.' };
  }

  if (parsed.kind === 'clear') {
    return { ok: true, kind: 'clear', displayText: 'Clear', animation: null, data: {} };
  }

  if (parsed.kind === 'dnd') {
    const rolls = Array.from({ length: parsed.count }, () => rollDie(parsed.sides));
    const sum = rolls.reduce((a, b) => a + b, 0);
    const total = sum + (parsed.modifier || 0);
    const modText = parsed.modifier ? (parsed.modifier > 0 ? ` +${parsed.modifier}` : ` ${parsed.modifier}`) : '';

    return {
      ok: true,
      kind: 'dnd',
      displayText: `${parsed.count}d${parsed.sides}${modText} = ${total}`,
      animation: `${parsed.count}d${parsed.sides}`,
      data: {
        rolls,
        sum,
        modifier: parsed.modifier || 0,
        total,
        count: parsed.count,
        sides: parsed.sides,
      },
    };
  }

  if (parsed.kind === 'duality') {
    const hope = rollDie(parsed.hopeSides || 12);
    const fear = rollDie(parsed.fearSides || 12);

    let outcome = 'critical';
    if (hope > fear) outcome = 'with_hope';
    else if (fear > hope) outcome = 'with_fear';

    let advDie = null;
    let advSigned = 0;
    if (parsed.advMode === 'advantage') {
      advDie = rollDie(6);
      advSigned = advDie;
    } else if (parsed.advMode === 'disadvantage') {
      advDie = rollDie(6);
      advSigned = -advDie;
    }

    const base = hope + fear;
    const total = base + advSigned + (parsed.modifier || 0);

    const outcomeText =
      outcome === 'critical'
        ? 'CRITICAL'
        : outcome === 'with_hope'
        ? 'with Hope'
        : 'with Fear';

    const parts = [`Hope ${hope}`, `Fear ${fear}`];
    if (advDie !== null) {
      parts.push(`${parsed.advMode === 'advantage' ? 'Adv' : 'Dis'} d6 ${advDie}`);
    }
    if (parsed.modifier) {
      parts.push(parsed.modifier > 0 ? `+${parsed.modifier}` : `${parsed.modifier}`);
    }

    // Build per-die animation array so Hope and Fear dice render in distinct colours.
    const animationDice = [
      { qty: 1, sides: parsed.hopeSides || 12, themeColor: DUALITY_HOPE_COLOR },
      { qty: 1, sides: parsed.fearSides || 12, themeColor: DUALITY_FEAR_COLOR },
    ];
    if (advDie !== null) {
      animationDice.push({ qty: 1, sides: 6 });
    }

    return {
      ok: true,
      kind: 'duality',
      displayText: `Duality: ${total} (${outcomeText}) [${parts.join(', ')}]`,
      animation: animationDice,
      data: {
        hope,
        fear,
        dualityMode: parsed.dualityMode || 'standard',
        outcome,
        base,
        advMode: parsed.advMode,
        advDie,
        advSigned,
        modifier: parsed.modifier || 0,
        total,
      },
    };
  }

  if (parsed.kind === 'multi') {
    let total = 0;
    const parts = [];
    const labelParts = [];

    parsed.dice.forEach((dieGroup, i) => {
      const rolls = Array.from({ length: dieGroup.count }, () => rollDie(dieGroup.sides));
      const sum = rolls.reduce((a, b) => a + b, 0);
      const signedSum = dieGroup.negative ? -sum : sum;
      total += signedSum;
      parts.push(`${dieGroup.count}d${dieGroup.sides}=${sum}`);
      const prefix = i === 0 ? '' : dieGroup.negative ? ' - ' : ' + ';
      labelParts.push(`${prefix}${dieGroup.count}d${dieGroup.sides}`);
    });

    total += parsed.modifier || 0;

    const label = labelParts.join('');
    const modText = parsed.modifier ? (parsed.modifier > 0 ? ` +${parsed.modifier}` : ` ${parsed.modifier}`) : '';

    return {
      ok: true,
      kind: 'multi',
      displayText: `${label}${modText} = ${total} [${parts.join(', ')}]`,
      // Roll all dice groups so onRollComplete receives results for every group.
      animation: parsed.dice.map((d) => ({ qty: d.count, sides: d.sides })),
      data: {
        dice: parsed.dice,
        parts,
        total,
        modifier: parsed.modifier || 0,
      },
    };
  }

  return { ok: false, error: 'Unsupported roll.' };
};

/**
 * Interpret the actual dice values returned by @3d-dice/dice-box's onRollComplete
 * callback and build the final displayText + data for a roll.
 *
 * @param {object} parsed  - Result of parseDiceCommand()
 * @param {Array}  diceResults - Array of result groups from dice-box's onRollComplete.
 *   Each group: { value: number (group total), rolls: [{value: number, ...}], ... }
 * @returns {{ displayText: string, data: object } | null}
 */
export const buildDisplayFromResults = (parsed, diceResults) => {
  if (!parsed || !Array.isArray(diceResults) || diceResults.length === 0) {
    return null;
  }

  if (parsed.kind === 'dnd') {
    const group = diceResults[0];
    if (!group) return null;
    const rolls = (group.rolls || []).map((r) => r.value);
    // Use individual roll values when available; fall back to the pre-computed
    // group total (group.value) if the rolls array is unexpectedly empty.
    const sum = rolls.length > 0 ? rolls.reduce((a, b) => a + b, 0) : (group.value ?? 0);
    const modifier = parsed.modifier || 0;
    const total = sum + modifier;
    const modText = modifier ? (modifier > 0 ? ` +${modifier}` : ` ${modifier}`) : '';
    return {
      displayText: `${parsed.count}d${parsed.sides}${modText} = ${total}`,
      data: { rolls, sum, modifier, total, count: parsed.count, sides: parsed.sides },
    };
  }

  if (parsed.kind === 'duality') {
    const hopeGroup = diceResults[0];
    const fearGroup = diceResults[1];
    const advGroup = diceResults[2] || null;
    if (!hopeGroup || !fearGroup) return null;

    const hope = hopeGroup.value;
    const fear = fearGroup.value;
    const advDie = advGroup ? advGroup.value : null;

    let outcome = 'critical';
    if (hope > fear) outcome = 'with_hope';
    else if (fear > hope) outcome = 'with_fear';

    let advSigned = 0;
    if (advDie !== null) {
      advSigned = parsed.advMode === 'disadvantage' ? -advDie : advDie;
    }

    const base = hope + fear;
    const modifier = parsed.modifier || 0;
    const total = base + advSigned + modifier;

    const outcomeText =
      outcome === 'critical'
        ? 'CRITICAL'
        : outcome === 'with_hope'
        ? 'with Hope'
        : 'with Fear';

    const parts = [`Hope ${hope}`, `Fear ${fear}`];
    if (advDie !== null) {
      parts.push(
        `${parsed.advMode === 'advantage' ? 'Adv' : 'Dis'} d6 ${advDie}`
      );
    }
    if (modifier) {
      parts.push(modifier > 0 ? `+${modifier}` : `${modifier}`);
    }

    return {
      displayText: `Duality: ${total} (${outcomeText}) [${parts.join(', ')}]`,
      data: {
        hope,
        fear,
        dualityMode: parsed.dualityMode || 'standard',
        outcome,
        base,
        advMode: parsed.advMode,
        advDie,
        advSigned,
        modifier,
        total,
      },
    };
  }

  if (parsed.kind === 'multi') {
    let total = 0;
    const parts = [];
    const labelParts = [];

    parsed.dice.forEach((dieGroup, i) => {
      const group = diceResults[i];
      if (!group) return;
      const sum = group.value;
      const signedSum = dieGroup.negative ? -sum : sum;
      total += signedSum;
      parts.push(`${dieGroup.count}d${dieGroup.sides}=${sum}`);
      const prefix = i === 0 ? '' : dieGroup.negative ? ' - ' : ' + ';
      labelParts.push(`${prefix}${dieGroup.count}d${dieGroup.sides}`);
    });

    const modifier = parsed.modifier || 0;
    total += modifier;

    const label = labelParts.join('');
    const modText = modifier ? (modifier > 0 ? ` +${modifier}` : ` ${modifier}`) : '';

    return {
      displayText: `${label}${modText} = ${total} [${parts.join(', ')}]`,
      data: { dice: parsed.dice, parts, total, modifier },
    };
  }

  return null;
};
